/**
 * Column descriptor for {@link toCsv}.
 *
 * @typeParam T - Row shape.
 */
export type CsvColumn<T> = {
  /** Header label written to the first CSV row. */
  header: string;
  /** Resolves the value to serialize from a row. */
  value: (row: T) => string | number | null | undefined;
};

/**
 * Characters that, as the leading character of a cell, cause spreadsheet apps
 * (Excel, Google Sheets, LibreOffice Calc) to interpret the value as a formula.
 * String values starting with one of these are prefixed with `'` so the cell is
 * rendered as literal text. See OWASP "CSV Injection".
 */
const FORMULA_INJECTION_PREFIXES = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Escape a single field per RFC 4180:
 *  - Empty/nullish values render as an empty string.
 *  - Numbers are stringified directly (negative numbers are preserved as-is).
 *  - Strings whose first character could trigger a spreadsheet formula are
 *    prefixed with `'` to neutralize CSV/Excel formula injection.
 *  - Strings containing `,`, `"`, `\n`, or `\r` are wrapped in quotes
 *    and any embedded `"` is doubled (`""`).
 */
const escapeField = (raw: string | number | null | undefined): string => {
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "number") return String(raw);
  if (raw === "") return "";
  const sanitized = FORMULA_INJECTION_PREFIXES.includes(raw[0]) ? `'${raw}` : raw;
  if (/[",\r\n]/.test(sanitized)) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
};

/**
 * Serialize rows into an RFC 4180 CSV string.
 * Lines are joined with CRLF as the spec requires.
 */
export const toCsv = <T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string => {
  const headerLine = columns.map((c) => escapeField(c.header)).join(",");
  const dataLines = rows.map((row) => columns.map((c) => escapeField(c.value(row))).join(","));
  return [headerLine, ...dataLines].join("\r\n");
};

/**
 * Trigger a browser download for the given CSV string.
 * Prepends a UTF-8 BOM so Excel opens the file with correct encoding.
 */
export const downloadCsv = (csv: string, filename: string): void => {
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
